/**
 * Étiquette d'expédition A6 (105×148mm) — Phase 87v
 *
 * Format : 1 page A6 portrait par commande. Imprime sur étiquettes adhésives
 * standard A6 ou A4 (l'utilisateur gère son imprimante).
 *
 * Contenu :
 *  - EXPÉDITEUR (haut-gauche, compact) : Maison Thaï + adresse
 *  - DESTINATAIRE (BLOC CENTRAL XL) : nom + rue + NPA + ville + téléphone
 *  - Référence commande + date expédition
 *  - QR code pointant vers la fiche commande publique (suivi client)
 *  - Mention "Produits frais 2-5°C" pour la chaîne du froid
 *
 * Pas affichée pour les commandes pickup (retrait sur place — pas de colis).
 */
import React from "react";
import { Document, Page, View, Text, Image, StyleSheet } from "@react-pdf/renderer";
import QRCode from "qrcode";
import { COMPANY_HEADER } from "../lib/company";

const PUBLIC_BASE_URL = "https://maison-thai.ch";

const styles = StyleSheet.create({
  page: { padding: 8, fontFamily: "Helvetica", color: "#0B0B0A", backgroundColor: "#FFFFFF" },
  outerFrame: {
    borderWidth: 1.5,
    borderColor: "#0B0B0A",
    borderStyle: "solid",
    padding: 10,
    height: "100%",
    flexDirection: "column",
  },

  // Bandeau supérieur : expéditeur + ref commande
  topRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    borderBottom: "1pt dashed #575A57",
    paddingBottom: 6,
    marginBottom: 8,
  },
  senderBlock: { flexDirection: "column", maxWidth: "55%" },
  senderKicker: { fontSize: 6.5, color: "#575A57", textTransform: "uppercase", letterSpacing: 1, marginBottom: 1 },
  senderName: { fontSize: 9, fontFamily: "Helvetica-Bold", color: "#0B0B0A" },
  senderLine: { fontSize: 7.5, color: "#575A57", lineHeight: 1.25 },
  refBlock: { flexDirection: "column", alignItems: "flex-end" },
  refKicker: { fontSize: 6.5, color: "#575A57", textTransform: "uppercase", letterSpacing: 1 },
  refValue: { fontSize: 10, fontFamily: "Helvetica-Bold", color: "#0B0B0A", marginTop: 1 },
  refDate: { fontSize: 7.5, color: "#575A57", marginTop: 1 },

  // Bloc destinataire (le plus visible)
  recipientKicker: {
    fontSize: 8,
    color: "#1F4E3D",
    fontFamily: "Helvetica-Bold",
    textTransform: "uppercase",
    letterSpacing: 1.5,
    marginBottom: 4,
  },
  recipientName: {
    fontSize: 18,
    fontFamily: "Helvetica-Bold",
    color: "#0B0B0A",
    marginBottom: 6,
    lineHeight: 1.15,
  },
  recipientAddrLine: {
    fontSize: 14,
    fontFamily: "Helvetica-Bold",
    color: "#0B0B0A",
    lineHeight: 1.3,
  },
  recipientContact: {
    fontSize: 9,
    color: "#575A57",
    marginTop: 6,
  },

  // Footer : QR + cold chain
  footer: {
    marginTop: "auto",
    flexDirection: "row",
    alignItems: "flex-end",
    justifyContent: "space-between",
    borderTop: "1pt dashed #575A57",
    paddingTop: 6,
  },
  coldBlock: { flexDirection: "column", maxWidth: "65%" },
  coldKicker: { fontSize: 7, color: "#C9971E", fontFamily: "Helvetica-Bold", textTransform: "uppercase", letterSpacing: 1 },
  coldText: { fontSize: 8, color: "#0B0B0A", marginTop: 2, lineHeight: 1.3 },
  qrBlock: { flexDirection: "column", alignItems: "center" },
  qrImg: { width: 60, height: 60 },
  qrCaption: { fontSize: 5.5, color: "#A3A6A3", marginTop: 1, textTransform: "uppercase", letterSpacing: 0.6 },
});

// Pré-génère le QR code en data URL avant rendu (react-pdf ne supporte pas Promises inline)
export async function generateShippingLabelBlob({ co, settings }, { pdf }) {
  // QR pointe vers la fiche client (compte utilisateur web — suivi commande)
  const qrUrl = `${PUBLIC_BASE_URL}/boutique/mes-commandes`;
  let qrDataUrl = "";
  try {
    qrDataUrl = await QRCode.toDataURL(qrUrl, {
      errorCorrectionLevel: "M",
      margin: 1,
      width: 200,
      color: { dark: "#0B0B0A", light: "#FFFFFF" },
    });
  } catch (e) {
    // Si la génération échoue, on rend sans QR plutôt que de planter
    qrDataUrl = "";
  }
  const doc = <ShippingLabelDocument co={co} settings={settings} qrDataUrl={qrDataUrl} />;
  return await pdf(doc).toBlob();
}

const addressLines = (addr) => {
  if (!addr) return [];
  return String(addr)
    .split(/\n|,\s/)
    .map((s) => s.trim())
    .filter(Boolean);
};

const fmtOrderDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
};

export const ShippingLabelDocument = ({ co, settings, qrDataUrl }) => {
  const cleanName = (co?.distributor_name || "").replace(/\s*\(web\)\s*$/i, "").trim();
  const addrLines = addressLines(co?.distributor_address);
  const contact = co?.distributor_contact || "";

  return (
    <Document>
      <Page size="A6" style={styles.page}>
        <View style={styles.outerFrame}>
          {/* Bandeau haut : expéditeur + ref */}
          <View style={styles.topRow}>
            <View style={styles.senderBlock}>
              <Text style={styles.senderKicker}>Expéditeur</Text>
              <Text style={styles.senderName}>{COMPANY_HEADER}</Text>
              {(settings?.company_address || "")
                .split("\n")
                .filter(Boolean)
                .map((line, i) => (
                  <Text key={i} style={styles.senderLine}>
                    {line}
                  </Text>
                ))}
            </View>
            <View style={styles.refBlock}>
              <Text style={styles.refKicker}>Commande</Text>
              <Text style={styles.refValue}>{co?.reference || "—"}</Text>
              {fmtOrderDate(co?.created_at) && (
                <Text style={styles.refDate}>Cmd. du {fmtOrderDate(co.created_at)}</Text>
              )}
            </View>
          </View>

          {/* Destinataire (BLOC PROÉMINENT) */}
          <View>
            <Text style={styles.recipientKicker}>📦 Destinataire</Text>
            <Text style={styles.recipientName}>{cleanName || "—"}</Text>
            {addrLines.length > 0 ? (
              addrLines.map((line, i) => (
                <Text key={i} style={styles.recipientAddrLine}>
                  {line}
                </Text>
              ))
            ) : (
              <Text style={styles.recipientAddrLine}>Adresse manquante</Text>
            )}
            {contact && <Text style={styles.recipientContact}>{contact}</Text>}
          </View>

          {/* Footer : cold chain + QR */}
          <View style={styles.footer}>
            <View style={styles.coldBlock}>
              <Text style={styles.coldKicker}>⚠ Produits frais</Text>
              <Text style={styles.coldText}>
                Conserver à 2-5 °C dès réception. Ouvrir et stocker au frais immédiatement.
              </Text>
            </View>
            {qrDataUrl ? (
              <View style={styles.qrBlock}>
                <Image src={qrDataUrl} style={styles.qrImg} />
                <Text style={styles.qrCaption}>Suivi commande</Text>
              </View>
            ) : null}
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default ShippingLabelDocument;
